import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import ControlRenderer from "./ControlRender";
import FieldVisibilityWrapper from './FieldVisibilityWrapper';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { EXTERNAL_API_URLS, ENTITY_NAMES } from "../../constants";
import { Control } from "../../type";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { isFormSectionValid } from "../../utils/formValidation";
import { useTenant } from '../TenantContext';

/**
 * Generic section for rendering entity attributes and controls.
 * Fetches attributes from TenantContext instead of API.
 *
 * @example
 * <EntityAttributesSection
 *   entityControl={entityControl}
 *   formValues={formValues}
 *   setFormValues={setFormValues}
 *   frmerrors={frmerrors}
 *   entitiesData={entitiesData}
 *   filterControls={customFilterFn}
 *   disableLogic={customDisableFn}
 * />
 */

interface EntityAttributesSectionProps {
    entityControl: Control;
    formValues: Record<string, string>;
    setFormValues: (controlId: string, value: any) => void;
    frmerrors: Record<string, string>;
    entitiesData?: Control[];
    filterControls?: (ctrl: Control) => boolean;
    disableLogic?: (ctrl: Control) => boolean;
    onAttributesFetched?: (attributes: Control[], entityName: string) => void;
    onValidityChange?: (isValid: boolean) => void;
    authorityId?: string;
    sectionTitle?: string;
    sectionDescription?: string;
    estimatedNetPrice?: string;
    onNavigateToSection?: (sectionKey: string) => void;
    tabAttributes?: Record<string, Control[]>;
    activeTab?: string;
    handleConnectWithUs?: () => void;
    isGlobalValid?: boolean;
    handleNext?: () => void;
    loadingNextTab?: boolean;
    handleBack?: () => void;
    isEditingFromSummary?: boolean;
    showErrors?: boolean;
    calculateDerivedAttributesForEntity?: (entityId: string, overrideBody?: any, currentEntityId?: string) => Promise<any>;
    estimateId?: string;
}

const EntityAttributesSection: React.FC<EntityAttributesSectionProps> = ({
    entityControl,
    formValues,
    setFormValues,
    frmerrors,
    entitiesData,
    filterControls,
    disableLogic,
    onAttributesFetched,
    onValidityChange,
    authorityId,
    sectionTitle,
    sectionDescription,
    estimatedNetPrice,
    onNavigateToSection,
    tabAttributes,
    activeTab,
    handleConnectWithUs,
    isGlobalValid,
    handleNext,
    loadingNextTab,
    handleBack,
    isEditingFromSummary = false,
    showErrors = false,
    calculateDerivedAttributesForEntity,
    estimateId,
}) => {
    const { getAttributesByEntityName, loading: tenantLoading } = useTenant();
    const [dynamicOptions, setDynamicOptions] = useState<Record<string, { loading: boolean; options: string[] | null }>>({});
    const prevDisabledStatesRef = useRef<Record<string, boolean>>({});

    // Helper function to convert state names to title case
    const toTitleCase = (str: string): string => {
        return str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
    };

    const onAttributesFetchedRef = useRef(onAttributesFetched);
    useEffect(() => {
        onAttributesFetchedRef.current = onAttributesFetched;
    }, [onAttributesFetched]);

    // Get attributes from TenantContext instead of API
    // Prefer processed attributes passed via `tabAttributes` (these may include
    // additional flags like `initiallyHidden` and `visible` set by the parent).
    const attributesSource = useMemo(() => {
        if (tenantLoading) return { attributes: [], fromTabAttributes: false };
        const ta = tabAttributes ? tabAttributes[entityControl.name] : undefined;
        if (ta && ta.length > 0) {
            return { attributes: ta, fromTabAttributes: true };
        }
        return { attributes: getAttributesByEntityName(entityControl.name), fromTabAttributes: false };
    }, [getAttributesByEntityName, entityControl.name, tenantLoading, tabAttributes]);

    const attributes = attributesSource.attributes;

    // Call onAttributesFetched when attributes are available
    // CRITICAL: Only call when attributes come from TenantContext (not from tabAttributes)
    // to prevent duplicate calls when attributes are already loaded in parent state
    useEffect(() => {
        if (attributes.length > 0 && !attributesSource.fromTabAttributes) {
            onAttributesFetchedRef.current?.(attributes, entityControl.name);
        }
    }, [attributes, entityControl.name, attributesSource.fromTabAttributes]);

    useEffect(() => {
        if (onValidityChange) {
            const isValid = isFormSectionValid(attributes, formValues);
            onValidityChange(isValid);
        }
    }, [attributes, formValues, onValidityChange]);

    // Fetch dynamic options for controls within the section (unchanged)
    const fetchDynamicOptionsForControl = useCallback(async (ctrl: Control) => {
        const controlKey = ctrl._id;
        const isHighSchoolControl = ctrl.possibleValuesSource?.includes('/highschools') || false;
        const isStateControl = ctrl.possibleValuesSource?.includes('/states') || false;
        const schoolText = isHighSchoolControl ? (formValues[controlKey] || '').trim() : '';

        if (!ctrl.possibleValuesSource || (isHighSchoolControl && schoolText.length === 0)) {
            setDynamicOptions(prev => ({ ...prev, [controlKey]: { loading: false, options: null } }));
            return;
        }

        setDynamicOptions(prev => ({ ...prev, [controlKey]: { loading: true, options: prev[controlKey]?.options ?? null } }));

        let apiUrl = ctrl.possibleValuesSource.trim().replaceAll('\n', '');
        if (isStateControl) {
            apiUrl = `${EXTERNAL_API_URLS.STATES}?AuthorityId=${encodeURIComponent(authorityId ?? '')}`;
        } else if (isHighSchoolControl) {
            apiUrl = `${EXTERNAL_API_URLS.HIGH_SCHOOLS}?schooltext=${encodeURIComponent(schoolText)}`;
        }

        try {
            const tenantName = localStorage.getItem('TENANT_NAME') ?? '';
            const normalizedTenantName = tenantName.trim().toLowerCase();
            const response = await fetch(apiUrl, {
                headers: {
                    'authority-name': normalizedTenantName
                }
            });
            if (!response.ok) throw new Error(`Failed to fetch options from ${apiUrl}`);
            const data = await response.json();

            let mappedOptions: string[] = [];
            if (Array.isArray(data)) {
                if (isStateControl) {
                    mappedOptions = data.map((item: any) => `${toTitleCase(item.name)} (${item.abbreviation})`);
                } else if (isHighSchoolControl) {
                    mappedOptions = data.map((item: any) => item.hsName);
                } else {
                    mappedOptions = data.map((item: any) => String(item.name ?? item.hsName ?? item.displayName ?? item._id ?? item)).filter(s => s.length > 0);
                }
            }
            const sortedOptions = [...mappedOptions].sort((a, b) => a.localeCompare(b));
            setDynamicOptions(prev => ({ ...prev, [controlKey]: { loading: false, options: sortedOptions } }));
        } catch (error) {
            console.error(`Error fetching dynamic options for ${ctrl.name}:`, error);
            setDynamicOptions(prev => ({ ...prev, [controlKey]: { loading: false, options: [] } }));
        }
    }, [authorityId, formValues]);

    // Effect for State control (runs only once when attributes are loaded)
    const stateFetchedRef = useRef(false);
    useEffect(() => {
        // Find State controls with possibleValuesSource
        const stateControl = attributes.find(attr => attr.possibleValuesSource?.includes('/states'));
        if (stateControl && !stateFetchedRef.current) {
            fetchDynamicOptionsForControl(stateControl);
            stateFetchedRef.current = true;
        }

        // Also handle State controls WITHOUT possibleValuesSource (e.g., StudentContact State)
        // by manually fetching state options using the same endpoint
        const stateControlWithoutSource = attributes.find(attr =>
            (attr.name?.toLowerCase() === 'state' || attr.friendlyName?.toLowerCase() === 'state')
            && !attr.possibleValuesSource
        );

        if (stateControlWithoutSource && !stateFetchedRef.current && authorityId) {
            const controlKey = stateControlWithoutSource._id;
            setDynamicOptions(prev => ({ ...prev, [controlKey]: { loading: true, options: null } }));

            const apiUrl = `${EXTERNAL_API_URLS.STATES}?AuthorityId=${encodeURIComponent(authorityId)}`;
            const tenantName = localStorage.getItem('TENANT_NAME') ?? '';
            const normalizedTenantName = tenantName.trim().toLowerCase();

            fetch(apiUrl, {
                headers: { 'authority-name': normalizedTenantName }
            })
                .then(response => {
                    if (!response.ok) throw new Error(`Failed to fetch states`);
                    return response.json();
                })
                .then(data => {
                    const mappedOptions = Array.isArray(data)
                        ? data.map((item: any) => `${toTitleCase(item.name)} (${item.abbreviation})`)
                        : [];
                    const sortedOptions = [...mappedOptions].sort((a, b) => a.localeCompare(b));
                    setDynamicOptions(prev => ({ ...prev, [controlKey]: { loading: false, options: sortedOptions } }));
                })
                .catch(error => {
                    console.error(`Error fetching state options for ${stateControlWithoutSource.name}:`, error);
                    setDynamicOptions(prev => ({ ...prev, [controlKey]: { loading: false, options: [] } }));
                });

            stateFetchedRef.current = true;
        }
    }, [attributes, fetchDynamicOptionsForControl, authorityId]);

    // Effect for High School control (runs on value change)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const highSchoolControl = useMemo(() => attributes.find(attr => attr.possibleValuesSource?.includes('/highschools')), [attributes]);
    const highSchoolValue = highSchoolControl ? formValues[highSchoolControl._id] : undefined;

    useEffect(() => {
        if (highSchoolControl) {
            const handler = setTimeout(() => {
                fetchDynamicOptionsForControl(highSchoolControl);
            }, 300); // Debounce
            return () => clearTimeout(handler);
        }
    }, [highSchoolControl, highSchoolValue, fetchDynamicOptionsForControl]);

    const controlsToRender = useMemo(() => {
        if (!filterControls) return attributes;
        return attributes.filter(ctrl => filterControls(ctrl));
    }, [attributes, filterControls]);

    // Field visibility is handled by the external `FieldVisibilityWrapper` component.

    const disabledStates = useMemo(() => {
        const states: Record<string, boolean> = {};
        for (const ctrl of controlsToRender) {
            states[ctrl._id] = disableLogic ? disableLogic(ctrl) : false;
        }

        // Debug: log enablingAttribute and computed disabled state for diagnosis
        try {
            for (const ctrl of controlsToRender) {
                if (ctrl.enablingAttribute) {
                    // enablingAttribute logic can be added here if needed for debugging
                }
            }
        } catch (err) {
            console.error('Error while computing disabledStates debug info:', err);
        }
        return states;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controlsToRender, disableLogic]);

    // Only clear values for controls that just became disabled
    useEffect(() => {
        const prevDisabledStates = prevDisabledStatesRef.current;
        const controlsToClear: string[] = [];
        for (const ctrl of controlsToRender) {
            const isNowDisabled = disabledStates[ctrl._id];
            const wasPreviouslyDisabled = prevDisabledStates[ctrl._id] ?? false;
            if (isNowDisabled && !wasPreviouslyDisabled && formValues[ctrl._id] !== undefined) {
                controlsToClear.push(ctrl._id);
            }
        }
        if (controlsToClear.length > 0) {
            for (const controlId of controlsToClear) {
                setFormValues(controlId, undefined);
            }
        }
        prevDisabledStatesRef.current = disabledStates;
        // Only run when disabledStates or controlsToRender change
    }, [disabledStates, controlsToRender, setFormValues, formValues]);

    if (tenantLoading) {
        const showRequiredFieldNote = attributes.some(attr => attr.isRequired);
        return (
            <div className="w-full">
                {(sectionTitle || sectionDescription) && (
                    <div className="mb-6">
                        {sectionTitle && <h2 className="text-[26px] font-bold text-gray-800">{sectionTitle}</h2>}
                        {sectionDescription && <p className="text-gray-600 mt-1 text-[20px]">{sectionDescription}</p>}
                        {showRequiredFieldNote && entityControl.name !== ENTITY_NAMES.SUMMARY && (
                            <p className="text-gray-600 mt-1 text-[20px]">*Indicates a required field.</p>
                        )}
                    </div>
                )}
                <div className="flex flex-col rounded-lg">
                    <div className="w-full mb-4">
                        <div className="animate-pulse bg-gray-200 h-10 rounded"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full">
            {(sectionTitle || sectionDescription) && (
                <div className="mb-6">
                    {sectionTitle && <h2 className="text-[26px] font-bold text-gray-800">{sectionTitle}</h2>}
                    {sectionDescription && <p className="text-gray-600 mt-1 text-[20px]">{sectionDescription}</p>}
                    {attributes.some(attr => attr.isRequired) && entityControl.name !== ENTITY_NAMES.SUMMARY && (
                        <p className="text-gray-600 mt-1 text-[20px]">*Indicates a required field.</p>
                    )}
                </div>
            )}
            <div className="flex flex-col rounded-lg">
                {controlsToRender.map(ctrl => {
                    const isDisabled = disabledStates[ctrl._id];
                    const dynamicProps = dynamicOptions[ctrl._id] || { loading: false, options: null };

                    // Render the control content first
                    const controlContent = (
                        <FieldVisibilityWrapper control={ctrl}>
                            <ControlRenderer
                                ctrl={{
                                    ...ctrl,
                                    displayName: ctrl.isRequired ? `${ctrl.displayName}` : ctrl.displayName,
                                }}
                                formValues={formValues}
                                onChange={setFormValues}
                                errors={frmerrors}
                                disabled={isDisabled}
                                entityName={entityControl.name}
                                dynamicOptions={dynamicProps.options}
                                isLoadingOptions={dynamicProps.loading}
                                estimatedNetPrice={estimatedNetPrice}
                                onNavigateToSection={onNavigateToSection}
                                tabAttributes={tabAttributes}
                                activeTab={activeTab}
                                handleConnectWithUs={handleConnectWithUs}
                                isGlobalValid={isGlobalValid}
                                handleNext={handleNext}
                                loadingNextTab={loadingNextTab}
                                handleBack={handleBack}
                                isEditingFromSummary={isEditingFromSummary}
                                showErrors={showErrors}
                                calculateDerivedAttributesForEntity={calculateDerivedAttributesForEntity}
                                estimateId={estimateId}
                            />
                        </FieldVisibilityWrapper>
                    );

                    // For Summary entity, check if this is the main control that renders content
                    const isSummaryEntity = entityControl.name === ENTITY_NAMES.SUMMARY;
                    const isMainSummaryControl = ctrl.name === 'Your Estimated Net Price';

                    // Only render wrapper div if it's not a Summary entity OR if it's the main Summary control
                    if (!isSummaryEntity || isMainSummaryControl) {
                        return (
                            <div
                                key={ctrl._id}
                                className={`w-full mb-4${isDisabled ? " rounded" : ""}`}
                            >
                                {controlContent}
                            </div>
                        );
                    }

                    // For other Summary controls, return the content directly (will be null anyway)
                    return <React.Fragment key={ctrl._id}>{controlContent}</React.Fragment>;
                })}
            </div>
        </div>
    );
};

export default EntityAttributesSection;